import { Link, useParams } from 'react-router-dom';

import { PersonType } from '../../../../../../../../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useGetSubsetPersons } from '@/api/queries/useGetSubsetPersons';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';

const KeyEntitiesSectionLoader = () => {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[...Array(6)].map((_, index) => (
        <Skeleton key={index} className="h-8 w-full bg-gray-200" />
      ))}
    </div>
  );
};

export const KeyEntitiesSection = () => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { data: persons, isLoading: personsIsLoading } = useGetSubsetPersons(matterId, subsetId, 6);

  if (!personsIsLoading && (!persons || persons?.length === 0)) {
    return null;
  }

  return (
    <div className="h-full basis-1/2">
      <div className="flex h-full flex-col rounded-md border border-gray-200 bg-white">
        <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 p-3 px-4">
          <div className="flex items-center gap-3">
            <Icon className="lucide--users-round h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Key Entities</h3>
          </div>

          <Button asChild variant="ghost" className="p-0 text-xs text-blue-500 underline hover:text-blue-700" size="sm">
            <Link
              to={`/matters/${matterId}/subsets/${subsetId}/entities`}
              onClick={() => trackCustomEvent(TrackingEventsV2.PersonViewAllOnSummary)}
            >
              View Subset Entities
              <Icon className="lucide--external-link h-3 w-3" />
            </Link>
          </Button>
        </div>

        <div className="flex-1 p-4">
          {personsIsLoading ? (
            <KeyEntitiesSectionLoader />
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {persons.map((entity) => {
                const factsUrl = `/matters/${matterId}/subsets/${subsetId}/facts?${new URLSearchParams({
                  personId: entity.id,
                }).toString()}`;
                return (
                  <Link
                    key={entity.id}
                    to={factsUrl}
                    onClick={() => trackCustomEvent(TrackingEventsV2.PersonClickKeyFromHome)}
                    className="group flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 p-2 px-2.5 transition-all hover:border-gray-300 hover:bg-gray-100"
                  >
                    {entity.type === PersonType.Person && (
                      <Icon className="lucide--user-round h-3 w-3 shrink-0 text-gray-500 group-hover:text-gray-700" />
                    )}
                    {entity.type === PersonType.Organisation && (
                      <Icon className="lucide--building h-3 w-3 shrink-0 text-gray-500 group-hover:text-gray-700" />
                    )}
                    {entity.type === PersonType.Place && (
                      <Icon className="lucide--map-pin h-3 w-3 shrink-0 text-gray-500 group-hover:text-gray-700" />
                    )}
                    <span className="truncate text-xs font-medium text-gray-700 group-hover:text-gray-900">
                      {entity.name}
                    </span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
